import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatMoney } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { projectService } from '../../services/projectService';
import { workCategoryService } from '../../services/workCategoryService';
import { ProjectFormData, WorkCategory, WorkStatus, WorkStatusLabels } from '../../types';
import { DEFAULT_CURRENCY } from '../../types/currency';
import { ProjectStatusBadge } from '../../components/Projects/ProjectStatusBadge';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { showError } from '../../components/Alerts';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  FileText,
  Edit,
  Calendar,
  DollarSign
} from 'lucide-react';
import { PERMISSIONS } from '../../constants/permissions';
import RequirePermission from '../../components/RequirePermission';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectFormData | null>(null);
  const [allWorkCategories, setAllWorkCategories] = useState<WorkCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAllWorkCategories = useCallback(async () => {
    try {
      const categories = await workCategoryService.getAllWorkCategories();
      setAllWorkCategories(categories);
    } catch (error) {
      console.error('Error loading work categories:', error);
    }
  }, []);

  const loadProjectData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const projectId = parseInt(id!);

      // First, load the project data separately to ensure it loads
      try {
        const projectData = await projectService.getById(projectId);
        setProject(projectData);
      } catch (error) {
        console.error('Error loading project:', error);
        setError('Error loading project data');
        setLoading(false);
        return;
      }

      // Related data loading removed - not needed for detail view
    } catch (error) {
      console.error('Error loading project data:', error);
      setError('Error al cargar los datos del proyecto');
      showError('Error al cargar los datos del proyecto');
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadProjectData();
    }
    loadAllWorkCategories();
  }, [id, loadProjectData, loadAllWorkCategories]);

  const handleBack = () => {
    navigate('/projects');
  };

  const handleEditProject = () => {
    navigate(`/projects/${id}/edit`);
  };

  if (loading) {
    return <LoadingScreen message="Cargando proyecto..." />;
  }

  if (error || !project) {
    return (
      <div className="w-full h-full p-6">
        <div className="max-w-md mx-auto">
          <div className="p-6 text-center border rounded-lg bg-card border-border">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium text-foreground">
              {error ? 'Error al cargar' : 'Proyecto no encontrado'}
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
              {error || 'El proyecto que buscas no existe'}
            </p>
            <Button onClick={handleBack} variant="outline" className="rounded-lg">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver a Proyectos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-6 space-y-6">
      {/* Card Container */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        {/* Header */}
        <div className="px-6 py-5 border-b border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleBack} className="flex items-center gap-2 rounded-lg">
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Detalle del Proyecto</h1>
                <p className="mt-1 text-sm text-muted-foreground">Visualización completa del proyecto</p>
              </div>
            </div>
            <RequirePermission permission={PERMISSIONS.PROYECTOS.EDITAR}>
              <Button onClick={handleEditProject} variant="outline" className="flex items-center gap-2 rounded-lg">
                <Edit className="w-4 h-4" />
                Editar Proyecto
              </Button>
            </RequirePermission>
          </div>
        </div>

        {/* Información Básica del Proyecto */}
        <div className="px-6 py-6 space-y-8">
          {/* Información Básica */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Building2 className="w-5 h-5 text-primary" />
                Información Básica
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Datos principales del proyecto de construcción.
              </p>
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Nombre del Proyecto</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm font-medium text-foreground">{project.name}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Código de Obra</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">{project.workCode || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Tipo de Obra</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">{project.buildingType || 'N/A'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Estado</Label>
                  <div className="p-3">
                    <ProjectStatusBadge
                      status={project.projectStatus || WorkStatus.EN_CONSTRUCCION}
                      label={WorkStatusLabels[project.projectStatus || WorkStatus.EN_CONSTRUCCION]}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase text-muted-foreground">Descripción</Label>
                <div className="p-3 border rounded-lg bg-muted/50 border-border">
                  <p className="text-sm whitespace-pre-wrap text-foreground">{project.description || 'Sin descripción'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Cliente */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Users className="w-5 h-5 text-primary" />
                Cliente
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Información del cliente asociado al proyecto.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase text-muted-foreground">Nombre del Cliente</Label>
                <div className="p-3 border rounded-lg bg-muted/50 border-border">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <p className="text-sm text-foreground">{project.client || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Dirección */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <MapPin className="w-5 h-5 text-primary" />
                Dirección
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Ubicación física del proyecto de construcción.
              </p>
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Calle</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">{project.street || 'N/A'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Número</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">{project.streetNumber || 'N/A'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Ciudad</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">{project.city || 'N/A'}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Código Postal</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">{project.postalCode || 'N/A'}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">País</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">{project.country || 'Argentina'}</p>
                  </div>
                </div>
              </div>

              {project.latitude && project.longitude && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Ver en Google Maps</Label>
                  <Button
                    variant="outline"
                    className="w-full gap-2 rounded-lg"
                    onClick={() => {
                      window.open(`https://www.google.com/maps?q=${project.latitude},${project.longitude}`, '_blank');
                    }}
                  >
                    <MapPin className="w-4 h-4" />
                    Abrir en Google Maps
                  </Button>
                </div>
              )}

              {project.latitude && project.longitude && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Mapa Visual</Label>
                  <div className="w-full h-64 overflow-hidden border rounded-lg border-border">
                    <iframe
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${(project.longitude || -58.3816) - 0.01},${(project.latitude || -34.6037) - 0.01},${(project.longitude || -58.3816) + 0.01},${(project.latitude || -34.6037) + 0.01}&layer=mapnik&marker=${project.latitude || -34.6037},${project.longitude || -58.3816}`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      title="Mapa del proyecto"
                    ></iframe>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Fechas del Proyecto */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Calendar className="w-5 h-5 text-primary" />
                Fechas del Proyecto
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Período de ejecución del proyecto.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Fecha de Inicio</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">
                      {project.startDate ? new Date(project.startDate).toLocaleDateString('es-AR') : 'No definida'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Fecha de Fin</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm text-foreground">
                      {project.endDate ? new Date(project.endDate).toLocaleDateString('es-AR') : 'No definida'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border"></div>

          {/* Información Financiera */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <DollarSign className="w-5 h-5 text-primary" />
                Información Financiera
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Presupuesto, costos y valores del proyecto.
              </p>
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Moneda</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm font-medium text-foreground">
                      {project.currency || DEFAULT_CURRENCY}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Presupuesto</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm font-semibold text-amount">
                      {project.currency || DEFAULT_CURRENCY} {formatMoney(project.budget)}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Costo Actual</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm font-semibold text-amount">
                      {project.currency || DEFAULT_CURRENCY} {formatMoney(project.actualCost)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">M² Totales</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm font-medium text-foreground">
                      {(project.totalM2 || 0).toLocaleString('es-AR')} m²
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Costo por M²</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm font-semibold text-amount">
                      {project.currency || DEFAULT_CURRENCY} {formatMoney(project.costPerM2)}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-medium uppercase text-muted-foreground">Costo Total Estimado</Label>
                  <div className="p-3 border rounded-lg bg-muted/50 border-border">
                    <p className="text-sm font-bold text-amount">
                      {project.currency || DEFAULT_CURRENCY} {formatMoney(project.totalEstimatedCost)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rubros de Construcción */}
      {project.workCategories && project.workCategories.length > 0 && (
        <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
          <div className="px-6 py-5 border-b border-border">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <FileText className="w-5 h-5 text-primary" />
              Rubros de Construcción
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Distribución de costos por rubro
            </p>
          </div>
          <div className="p-6 space-y-3">
            {project.workCategories.map((wc) => {
              const category = allWorkCategories.find(c => c.id === wc.id);
              const amount = (project.totalEstimatedCost || 0) * (wc.percentage / 100);
              return (
                <div key={wc.id} className="flex items-center justify-between p-4 transition-all border rounded-lg bg-card border-border hover:shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{category?.value || `Rubro #${wc.id}`}</h4>
                      {category?.description && (
                        <p className="text-xs text-muted-foreground">{category.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Incidencia</div>
                      <div className="text-lg font-bold text-primary">{wc.percentage}%</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Monto Estimado</div>
                      <div className="text-lg font-bold text-amount">
                        {project.currency || DEFAULT_CURRENCY} {formatMoney(amount)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Total */}
            <div className="flex items-center justify-between p-4 mt-4 border-2 rounded-lg bg-primary/5 border-primary/20">
              <div className="font-semibold text-foreground">TOTAL</div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Suma Porcentajes</div>
                  <div className="text-lg font-bold text-primary">
                    {project.workCategories.reduce((sum, wc) => sum + wc.percentage, 0)}%
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Costo Total Estimado</div>
                  <div className="text-lg font-bold text-amount">
                    {project.currency || DEFAULT_CURRENCY} {formatMoney(project.totalEstimatedCost)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetail; 